<script setup>
import { computed, ref } from "vue";
import { useRoute } from "vue-router";
import { localeInfo, locales } from "../data/content";
import BrandMark from "./BrandMark.vue";
import IconArrow from "./IconArrow.vue";

const props = defineProps({
  locale: { type: String, required: true },
  nav: { type: Object, required: true },
  path: { type: String, default: "" },
});

const route = useRoute();
const menuOpen = ref(false);
const home = computed(() => `/${props.locale}`);
const themeLabel = computed(() =>
  props.locale === "es"
    ? "Cambiar entre modo claro y oscuro"
    : props.locale === "en"
      ? "Switch between light and dark mode"
      : "Passa dalla modalità chiara a quella scura",
);

function localePath(nextLocale) {
  const suffix = route.fullPath.replace(/^\/(es|en|it)/, "");
  return `/${nextLocale}${suffix}`;
}

function closeMenu() {
  menuOpen.value = false;
}

function toggleTheme() {
  const root = document.documentElement;
  const next = root.dataset.theme === "light" ? "dark" : "light";

  if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
    root.dataset.theme = next;
    localStorage.setItem("resi-theme", next);
    return;
  }

  if (root.classList.contains("theme-is-changing")) return;
  root.dataset.themeTransition = next;
  root.classList.add("theme-is-changing");

  window.setTimeout(() => {
    root.dataset.theme = next;
    localStorage.setItem("resi-theme", next);
  }, next === "dark" ? 900 : 1020);

  window.setTimeout(() => {
    root.classList.remove("theme-is-changing");
    delete root.dataset.themeTransition;
  }, 1940);
}
</script>

<template>
  <header class="site-header">
    <div class="site-header__inner shell">
      <RouterLink class="brand-link" :to="home" aria-label="Resi Corporation">
        <BrandMark />
      </RouterLink>

      <nav :class="['primary-nav', { 'is-open': menuOpen }]" aria-label="Principal">
        <RouterLink :to="`${home}#services`" @click="closeMenu">{{ nav.services }}</RouterLink>
        <RouterLink :to="`${home}#projects`" @click="closeMenu">{{ nav.projects }}</RouterLink>
        <RouterLink :to="`${home}#process`" @click="closeMenu">{{ nav.process }}</RouterLink>
        <RouterLink :to="`${home}/associates`" @click="closeMenu">{{ nav.associates }}</RouterLink>
        <RouterLink :to="`${home}/blog`" @click="closeMenu">{{ nav.blog }}</RouterLink>
        <RouterLink class="nav-contact" :to="`${home}#contact`" @click="closeMenu">
          <span>{{ nav.contact }}</span>
          <IconArrow />
        </RouterLink>
      </nav>

      <div class="header-tools">
        <div class="locale-switcher" aria-label="Idiomas">
          <RouterLink
            v-for="item in locales"
            :key="item"
            :class="{ 'is-active': item === locale }"
            :to="localePath(item)"
            :lang="localeInfo[item].htmlLang"
            :aria-current="item === locale ? 'page' : undefined"
            :aria-label="localeInfo[item].name"
            :title="localeInfo[item].name"
          >
            <svg v-if="item === 'es'" width="24" height="16" viewBox="0 0 36 24" aria-hidden="true">
              <rect width="36" height="24" fill="#AA151B" />
              <rect y="6" width="36" height="12" fill="#F1BF00" />
            </svg>
            <svg v-else-if="item === 'en'" width="24" height="16" viewBox="0 0 36 24" aria-hidden="true">
              <rect width="36" height="24" fill="#012169" />
              <path d="M0 0 36 24M36 0 0 24" stroke="#FFF" stroke-width="5.2" />
              <path d="M0 0 36 24M36 0 0 24" stroke="#C8102E" stroke-width="2.2" />
              <path d="M18 0v24M0 12h36" stroke="#FFF" stroke-width="8" />
              <path d="M18 0v24M0 12h36" stroke="#C8102E" stroke-width="4.4" />
            </svg>
            <svg v-else width="24" height="16" viewBox="0 0 36 24" aria-hidden="true">
              <rect width="12" height="24" fill="#009246" />
              <rect x="12" width="12" height="24" fill="#FFF" />
              <rect x="24" width="12" height="24" fill="#CE2B37" />
            </svg>
          </RouterLink>
        </div>

        <button class="icon-button theme-toggle" type="button" :aria-label="themeLabel" :title="themeLabel" @click="toggleTheme">
          <span class="theme-toggle__sun">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <circle cx="12" cy="12" r="3.5" stroke="currentColor" stroke-width="1.6" />
              <path d="M12 2v2M12 20v2M4.93 4.93l1.42 1.42M17.65 17.65l1.42 1.42M2 12h2M20 12h2M4.93 19.07l1.42-1.42M17.65 6.35l1.42-1.42" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" />
            </svg>
          </span>
          <span class="theme-toggle__moon">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <path d="M20 15.4A8.4 8.4 0 0 1 8.6 4a8.6 8.6 0 1 0 11.4 11.4Z" stroke="currentColor" stroke-width="1.6" />
            </svg>
          </span>
        </button>

        <button
          class="icon-button menu-toggle"
          type="button"
          :aria-label="nav.menu"
          :aria-expanded="menuOpen"
          @click="menuOpen = !menuOpen"
        >
          <svg v-if="menuOpen" width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <path d="m6 6 12 12M18 6 6 18" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" />
          </svg>
          <svg v-else width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <path d="M4 7h16M4 12h16M4 17h16" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" />
          </svg>
        </button>
      </div>
    </div>
  </header>

  <div class="eclipse-layer" aria-hidden="true">
    <div class="eclipse-layer__sky" />
    <div class="eclipse-layer__geometry"><span /><span /><span /></div>
    <div class="eclipse-layer__orb">
      <span class="eclipse-layer__corona" />
      <span class="eclipse-layer__moon" />
    </div>
    <div class="eclipse-layer__temple"><span /><span /><span /><span /><span /><span /><span /></div>
    <div class="eclipse-layer__horizon" />
    <div class="eclipse-layer__legend"><span>NOX</span><i /><span>AURORA</span></div>
  </div>
</template>
